import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import {
    initLocationState as initSensorLocationState,
    isLocationState as isSensorLocationState,
} from 'src/sensors/locationStateContext.ts';
import { useContext, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { usePriceQuery } from 'src/projects/hooks/usePriceQuery.ts';
import {
    FetchSensorSelectionDocument,
    FetchSensorSelectionQuery,
    FetchSensorSelectionQueryVariables,
} from 'src/generated/operations.ts';
import { SelectionDataValue } from 'src/projects/components/SelectionDataContext.ts';
import { safeTotal } from 'src/projects/utils/SafeTotal.ts';
import { calculateTotals as calculateSensorTotals } from 'src/sensors/checkout/CalculateTotals.ts';
import { Provide, ProviderProps } from 'src/projects/components/SelectionDataProvider.tsx';
import { removeDuplicateFragments } from 'src/utils/de-dupe-fragments.ts';

const FetchSensorSelectionDedupedDocument = removeDuplicateFragments(FetchSensorSelectionDocument);

export const SensorProvider = ({ selection, children }: ProviderProps) => {
    const locationState = readSearchParam(
        new URLSearchParams(selection.searchQuery),
        LOCATION_STORE_PARAM_NAME,
        { ...initSensorLocationState },
        isSensorLocationState,
    );
    const { userSettings, currency } = useContext(ConfigContext);

    const { data } = usePriceQuery<FetchSensorSelectionQuery, FetchSensorSelectionQueryVariables>(
        FetchSensorSelectionDedupedDocument,
        {
            variables: {
                label: locationState.sensorLabel,
                locale: userSettings.currentLocale,
                reigon: userSettings.currentRegion,
            },
        },
    );

    const value = useMemo<SelectionDataValue>(() => {
        if (data === undefined) {
            return { loading: true, total: null, currency: currency ?? 'USD', data: undefined };
        }
        const { total, currency: cur } = safeTotal(
            () => calculateSensorTotals(
                data.accessories,
                userSettings,
                locationState,
                currency,
                locationState.pipeConnectors,
            ),
            currency ?? 'USD',
        );
        return { loading: false, total, currency: cur, data };
    }, [data, currency, userSettings, locationState]);

    return <Provide value={value}>{children}</Provide>;
};
